
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { TrendingDown, ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { BallTendency } from "@/hooks/level3/types";

interface IronScoreButtonsProps {
  onScoreChange: (score: number, tendency?: BallTendency) => void;
}

const IronScoreButtons: React.FC<IronScoreButtonsProps> = ({ onScoreChange }) => {
  const { t } = useTranslation('training');
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [showTendency, setShowTendency] = useState(false);

  const handleScoreClick = (score: number) => {
    setSelectedScore(score);
    
    // Only show tendency selector for balls that landed on the green (scores > 0)
    if (score > 0) {
      setShowTendency(true);
    } else {
      // For balls that missed the green, record immediately with no tendency
      onScoreChange(score, null);
      setSelectedScore(null);
    }
  };

  const handleTendencyClick = (tendency: BallTendency) => {
    if (selectedScore !== null) {
      onScoreChange(selectedScore, tendency);
      setSelectedScore(null);
      setShowTendency(false);
    }
  };

  const resetSelection = () => {
    setSelectedScore(null);
    setShowTendency(false);
  };

  if (showTendency) {
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 p-3 rounded-md border border-blue-100 text-center mb-2">
          <p className="text-blue-800 font-medium">{t('level3.scoring.tendency.selectTendency')}</p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Button 
            onClick={() => handleTendencyClick('left')}
            className="h-12 bg-gray-100 hover:bg-gray-200 text-gray-800 flex flex-col items-center"
          >
            <ArrowLeft className="h-5 w-5 mb-1" />
            <span className="text-xs">{t('level3.scoring.tendency.left')}</span>
          </Button>
          <Button 
            onClick={() => handleTendencyClick('center')}
            className="h-12 bg-gray-100 hover:bg-gray-200 text-gray-800 flex flex-col items-center"
          >
            <TrendingDown className="h-5 w-5 mb-1" />
            <span className="text-xs">{t('level3.scoring.tendency.center')}</span>
          </Button>
          <Button 
            onClick={() => handleTendencyClick('right')}
            className="h-12 bg-gray-100 hover:bg-gray-200 text-gray-800 flex flex-col items-center"
          >
            <ArrowRight className="h-5 w-5 mb-1" />
            <span className="text-xs">{t('level3.scoring.tendency.right')}</span>
          </Button>
        </div>
        <Button 
          onClick={resetSelection}
          className="h-10 w-full text-sm bg-gray-200 hover:bg-gray-300 text-gray-700"
        >
          {t('level3.scoring.tendency.cancel')}
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-2">
      <Button
        onClick={() => handleScoreClick(6)}
        className="h-12 font-medium bg-emerald-500 hover:bg-emerald-600 text-white"
      >
        {t('level3.scoring.iron.circle2m')}
      </Button>
      <Button
        onClick={() => handleScoreClick(4)}
        className="h-12 font-medium bg-green-500 hover:bg-green-600 text-white"
      >
        {t('level3.scoring.iron.circle4m')}
      </Button>
      <Button
        onClick={() => handleScoreClick(2)}
        className="h-12 font-medium bg-amber-400 hover:bg-amber-500 text-white"
      >
        {t('level3.scoring.iron.circle6m')}
      </Button>
      <Button
        onClick={() => handleScoreClick(1)}
        className="h-12 font-medium bg-orange-500 hover:bg-orange-600 text-white"
      >
        {t('level3.scoring.iron.circle10m')}
      </Button>
      <Button
        onClick={() => handleScoreClick(0)}
        className="h-12 font-medium bg-red-500 hover:bg-red-600 text-white"
      >
        {t('level3.scoring.iron.offTarget')}
      </Button>
    </div>
  );
};

export default IronScoreButtons;
